import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import AraSec from '../../ui/AraSec';
import Ipucu from '../../ui/Ipucu';
import TutarGiris from '../../ui/TutarGiris';
import { Secim } from '../kasa/Cekmeceler';
import { PARA_SIMGE, bugunIso, gunYaz, paraYaz, yeniAnahtar, type Para } from '../kasa/ortak';
import {
  DURUM_KISA, DURUM_ROZET, NAKITLI, TUR_AD, gorselAl, gorselYukle, islemAdi, kalanYaz,
  type Cek, type CekDurum, type CekHareketi, type CekSecenekler, type CekTur, type CekYon, type HesapSecenek,
} from './ortak';
import { Alan, Bilgi, Cekmece, Hata, NotSatiri, Uyari } from './parca';

/**
 * ÇEK / SENET ÇEKMECELERİ — yeni çek, durum değiştirme (tek ve toplu), ayrıntı + hareket geçmişi,
 * uyarı günü ayarı. Her seçim "yazdıkça ara" kutusudur; kısa ve sabit seçenekler düğme öbeğidir.
 */
const HESAP_TUR = { KASA: 'Kasa', BANKA: 'Banka', EMANET: 'Emanet' } as const;
const hesapSec = (h: HesapSecenek) => ({ id: h.id, ad: h.ad, alt: `${HESAP_TUR[h.tur]}${h.resmi ? '' : ' · gayriresmî'} · ${paraYaz(h.bakiyeKurus, h.para)}` });

export function useCekSecenekleri(acik: boolean) {
  const [s, setS] = useState<CekSecenekler | null>(null);
  const yukle = () => { if (acik) api<CekSecenekler>('/api/cek/secenekler').then(setS).catch(() => undefined); };
  useEffect(yukle, [acik]); // eslint-disable-line react-hooks/exhaustive-deps
  return { secenekler: s, yenile: yukle };
}

/* ─── Yeni çek / senet · düzenleme ───────────────────────────────────────── */
export function CekCekmece({ cek, yon: ilkYon, secenekler, gayriGorur, kapat, kaydedildi }: {
  cek: Cek | null; yon: CekYon; secenekler: CekSecenekler | null; gayriGorur: boolean; kapat: () => void;
  kaydedildi: (mesaj: string, uyarilar: string[]) => void;
}) {
  const [tur, setTur] = useState<CekTur>(cek?.tur ?? 'CEK');
  const [yon, setYon] = useState<CekYon>(cek?.yon ?? ilkYon);
  const [no, setNo] = useState(cek?.no ?? '');
  const [bankaAdi, setBankaAdi] = useState(cek?.bankaAdi ?? '');
  const [sube, setSube] = useState(cek?.sube ?? '');
  const [hesapNo, setHesapNo] = useState(cek?.hesapNo ?? '');
  const [kesideci, setKesideci] = useState(cek?.kesideci ?? '');
  const [cariId, setCariId] = useState(String((cek?.yon === 'VERILEN' ? cek?.supplierId : cek?.customerId) ?? ''));
  const [duzenleme, setDuzenleme] = useState(cek?.duzenlemeTarihi ?? bugunIso());
  const [vade, setVade] = useState(cek?.vadeTarihi ?? '');
  const [tutar, setTutar] = useState<number | null>(cek?.tutarKurus ?? null);
  const [para, setPara] = useState<Para>(cek?.para ?? 'TRY');
  const [resmi, setResmi] = useState(cek?.resmi ?? true);
  const [kendiHesap, setKendiHesap] = useState(String(cek?.kendiHesapId ?? ''));
  const [siparis, setSiparis] = useState(String(cek?.orderId ?? ''));
  const [satisFatura, setSatisFatura] = useState(String(cek?.satisFaturaId ?? ''));
  const [alisFatura, setAlisFatura] = useState(String(cek?.alisFaturaId ?? ''));
  const [not, setNot] = useState(cek?.not ?? '');
  const [gorsel, setGorsel] = useState<string | null>(cek?.gorsel ?? null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const anahtar = useRef(yeniAnahtar());

  const alinan = yon === 'ALINAN';
  const ilkDurum = !cek || cek.durum === 'PORTFOY' || cek.durum === 'BEKLIYOR';
  const belgeSec = (b: { id: number; ad: string; cariAd: string | null; kalanKurus: number }) => ({ id: b.id, ad: b.ad, alt: [b.cariAd, b.kalanKurus ? `kalan ${paraYaz(b.kalanKurus)}` : null].filter(Boolean).join(' · ') });
  // Cari seçildiyse o carinin belgeleri öne gelir.
  const cariOnce = <T extends { cariId: number | null }>(liste: T[]) => (cariId ? [...liste].sort((a, b) => Number(b.cariId === Number(cariId)) - Number(a.cariId === Number(cariId))) : liste);
  const hesaplar = useMemo(() => (secenekler?.hesaplar ?? []).filter((h) => h.para === para && (h.resmi || !resmi)), [secenekler, para, resmi]);

  async function dosyaSec(e: React.ChangeEvent<HTMLInputElement>) {
    const d = e.target.files?.[0];
    if (!d) return;
    setYukleniyor(true); setHata(null);
    try { setGorsel(await gorselYukle(d)); } catch (err) { setHata(err instanceof Error ? err.message : 'Görsel yüklenemedi'); }
    finally { setYukleniyor(false); e.target.value = ''; }
  }

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    if (!no.trim()) { setHata(`${TUR_AD[tur]} numarasını yazın.`); return; }
    if (!vade) { setHata('Vade tarihini seçin.'); return; }
    if (tutar == null || tutar <= 0) { setHata('Tutarı yazın.'); return; }
    if (!cariId && !kesideci.trim()) { setHata(alinan ? 'Çeki kimden aldığınızı seçin ya da keşidecinin adını yazın.' : 'Çeki kime verdiğinizi seçin ya da adını yazın.'); return; }
    setHata(null); setBekliyor(true);
    try {
      const govde = {
        tur, yon, no: no.trim(), bankaAdi: bankaAdi.trim() || null, sube: sube.trim() || null, hesapNo: hesapNo.trim() || null, kesideci: kesideci.trim() || null,
        customerId: alinan && cariId ? Number(cariId) : null, supplierId: !alinan && cariId ? Number(cariId) : null,
        duzenlemeTarihi: duzenleme || null, vadeTarihi: vade, tutarKurus: tutar, para, resmi,
        kendiHesapId: !alinan && kendiHesap ? Number(kendiHesap) : null,
        orderId: alinan && siparis ? Number(siparis) : null, satisFaturaId: alinan && satisFatura ? Number(satisFatura) : null,
        alisFaturaId: !alinan && alisFatura ? Number(alisFatura) : null, not: not.trim() || null, gorsel,
      };
      const r = cek
        ? await api<{ cek: Cek; uyarilar: string[] }>(`/api/cek/${cek.id}`, { method: 'PUT', body: JSON.stringify(govde) })
        : await api<{ cek: Cek; uyarilar: string[] }>('/api/cek', { method: 'POST', body: JSON.stringify({ ...govde, islemAnahtari: anahtar.current }) });
      kaydedildi(`${TUR_AD[r.cek.tur]} ${r.cek.no} kaydedildi (${paraYaz(r.cek.tutarKurus, r.cek.para)}, vade ${gunYaz(r.cek.vadeTarihi)}).`, r.uyarilar ?? []);
      kapat();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <Cekmece ust="Çek / Senet" baslik={cek ? `${TUR_AD[cek.tur]} ${cek.no} — düzenle` : 'Yeni çek / senet'} kapat={kapat} mesgul={bekliyor} genis>
      <form className="ccd-stack" onSubmit={kaydet}>
        {!cek && (
          <NotSatiri>
            Çek ya da senet girildiğinde <b>kasa defterine hareket yazılmaz</b>: portföydeki çek nakit değildir. Para, çek <b>"Tahsil edildi"</b> (verilen çekte <b>"Ödendi"</b>) olarak işaretlenince kasaya işlenir. <Ipucu id="cek-nakit-etkisi" />
          </NotSatiri>
        )}
        {cek && !ilkDurum && <div className="ccd-meta">Bu {TUR_AD[cek.tur].toLocaleLowerCase('tr')} "{cek.durumAd}" durumunda: tutarı, para birimi, türü ve resmî / gayriresmî ayrımı değiştirilemez. Değiştirmek için önce durumu geri alın.</div>}
        <Hata m={hata} />
        <div className="ccd-kasa-ikili">
          <Alan etiket="Tür"><Secim etiket="Tür" deger={tur} degis={setTur} disabled={!ilkDurum} secenekler={[['CEK', 'Çek'], ['SENET', 'Senet']] as const} /></Alan>
          <Alan etiket="Alınan mı, verilen mi" ipucu={<Ipucu id="cek-verilen" />}>
            <Secim etiket="Yön" deger={yon} degis={(y) => { setYon(y); setCariId(''); }} disabled={Boolean(cek)} secenekler={[['ALINAN', 'Alınan (müşteriden)'], ['VERILEN', 'Verilen (bizim)']] as const} />
          </Alan>
        </div>
        <div className="ccd-kasa-ikili">
          <Alan etiket={`${TUR_AD[tur]} no`} htmlFor="cek-no" zorunlu>
            <input id="cek-no" className="ccd-input" maxLength={40} autoFocus={!cek} value={no} onChange={(e) => setNo(e.target.value)} placeholder="Örn. 0012345" />
          </Alan>
          <Alan etiket={alinan ? 'Kimden alındı (müşteri)' : 'Kime verildi (tedarikçi)'} htmlFor="cek-cari">
            <AraSec id="cek-cari" yukleniyor={!secenekler} deger={cariId} degis={setCariId} bosEtiket="— kart yok —" yerTutucu={alinan ? 'Müşteri ara…' : 'Tedarikçi ara…'}
                    secenekler={alinan ? (secenekler?.musteriler ?? []) : (secenekler?.tedarikciler ?? [])} />
          </Alan>
        </div>
        <div className="ccd-kasa-ikili">
          <Alan etiket={alinan ? 'Keşideci (asıl borçlu)' : 'Kime (kartı yoksa adı)'} htmlFor="cek-kesideci"
                not={alinan ? 'Ciro ile gelen çekte müşteriden farklıdır; aynıysa boş bırakabilirsiniz.' : undefined}>
            <input id="cek-kesideci" className="ccd-input" maxLength={120} value={kesideci} onChange={(e) => setKesideci(e.target.value)} />
          </Alan>
          <Alan etiket="Banka" htmlFor="cek-banka" not="Yazdıkça daha önce girdiğiniz bankalar önerilir.">
            <input id="cek-banka" className="ccd-input" maxLength={80} list="cek-bankalar" value={bankaAdi} onChange={(e) => setBankaAdi(e.target.value)} placeholder={tur === 'SENET' ? 'Senette boş kalabilir' : 'Örn. Ziraat Bankası'} />
            <datalist id="cek-bankalar">{(secenekler?.bankalar ?? []).map((b) => <option key={b} value={b} />)}</datalist>
          </Alan>
        </div>
        <div className="ccd-kasa-ikili">
          <Alan etiket="Şube" htmlFor="cek-sube"><input id="cek-sube" className="ccd-input" maxLength={80} value={sube} onChange={(e) => setSube(e.target.value)} /></Alan>
          <Alan etiket="Hesap no" htmlFor="cek-hesapno"><input id="cek-hesapno" className="ccd-input" maxLength={40} value={hesapNo} onChange={(e) => setHesapNo(e.target.value)} /></Alan>
        </div>
        <div className="ccd-kasa-ikili">
          <Alan etiket="Düzenleme tarihi" htmlFor="cek-duzenleme"><input id="cek-duzenleme" className="ccd-input" type="date" value={duzenleme} onChange={(e) => setDuzenleme(e.target.value)} /></Alan>
          <Alan etiket="Vade tarihi" htmlFor="cek-vade" zorunlu><input id="cek-vade" className="ccd-input" type="date" value={vade} min={duzenleme || undefined} onChange={(e) => setVade(e.target.value)} /></Alan>
        </div>
        <div className="ccd-kasa-ikili">
          <Alan etiket="Tutar" htmlFor="cek-tutar" zorunlu><TutarGiris id="cek-tutar" kurus={tutar} degis={setTutar} simge={PARA_SIMGE[para]} disabled={!ilkDurum} /></Alan>
          <Alan etiket="Para birimi"><Secim etiket="Para birimi" deger={para} degis={setPara} disabled={!ilkDurum} secenekler={[['TRY', '₺ TL'], ['EUR', '€ Euro'], ['USD', '$ Dolar']] as const} /></Alan>
        </div>
        {gayriGorur && (
          <Alan etiket="Resmî mi" ipucu={<Ipucu id="cek-resmi-gayriresmi" />} not={resmi ? undefined : 'Gayriresmî çek / senedi yalnız faturasız tarafı görebilenler görür.'}>
            <Secim etiket="Resmî mi" deger={resmi ? 'E' : 'H'} degis={(d) => { setResmi(d === 'E'); setKendiHesap(''); }} disabled={!ilkDurum} secenekler={[['E', 'Resmî'], ['H', 'Gayriresmî']] as const} />
          </Alan>
        )}
        {!alinan && (
          <Alan etiket="Hangi hesabımızdan" htmlFor="cek-kendi" not="Çekin kesildiği banka hesabınız. Çek ödenince para bu hesaptan çıkar (ödeme sırasında değiştirebilirsiniz).">
            <AraSec id="cek-kendi" yukleniyor={!secenekler} deger={kendiHesap} degis={setKendiHesap} bosEtiket="— seçilmedi —" yerTutucu="Hesap ara…" secenekler={hesaplar.map(hesapSec)} />
          </Alan>
        )}
        <details className="ccd-cek-ek">
          <summary>Sipariş ya da faturaya bağla (isteğe bağlı)</summary>
          <div className="ccd-stack" style={{ marginTop: 12 }}>
            <div className="ccd-meta">Bağlarsanız nakit akışında o belgenin kalanı çek tutarı kadar düşülür; aynı para iki kez sayılmaz. <Ipucu id="nakit-akisi-cift-sayim" /></div>
            {alinan ? (
              <div className="ccd-kasa-ikili">
                <Alan etiket="Sipariş" htmlFor="cek-siparis"><AraSec id="cek-siparis" yukleniyor={!secenekler} deger={siparis} degis={setSiparis} bosEtiket="— bağlama —" yerTutucu="Sipariş no ya da cari…" secenekler={cariOnce(secenekler?.siparisler ?? []).map(belgeSec)} /></Alan>
                <Alan etiket="Satış faturası" htmlFor="cek-sfatura"><AraSec id="cek-sfatura" yukleniyor={!secenekler} deger={satisFatura} degis={setSatisFatura} bosEtiket="— bağlama —" yerTutucu="Fatura no ya da cari…" secenekler={cariOnce(secenekler?.satisFaturalari ?? []).map(belgeSec)} /></Alan>
              </div>
            ) : (
              <Alan etiket="Alış faturası" htmlFor="cek-afatura"><AraSec id="cek-afatura" yukleniyor={!secenekler} deger={alisFatura} degis={setAlisFatura} bosEtiket="— bağlama —" yerTutucu="Fatura no ya da tedarikçi…" secenekler={cariOnce(secenekler?.alisFaturalari ?? []).map(belgeSec)} /></Alan>
            )}
          </div>
        </details>
        <Alan etiket="Not" htmlFor="cek-not"><input id="cek-not" className="ccd-input" maxLength={500} value={not} onChange={(e) => setNot(e.target.value)} /></Alan>
        <Alan etiket="Çekin fotoğrafı" htmlFor="cek-gorsel" not="İsteğe bağlı. JPG, PNG ya da WEBP; en çok 8 MB.">
          <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input id="cek-gorsel" type="file" accept="image/jpeg,image/png,image/webp" onChange={dosyaSec} disabled={yukleniyor} />
            {yukleniyor && <span className="ccd-meta">Yükleniyor…</span>}
            {gorsel && !yukleniyor && <><span className="ccd-badge ccd-badge-success">Fotoğraf eklendi</span><button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setGorsel(null)}>Kaldır</button></>}
          </div>
        </Alan>
        <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor || yukleniyor}>{bekliyor ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </form>
    </Cekmece>
  );
}

/* ─── Durum değiştirme (tek çek ya da toplu) ─────────────────────────────── */
export function IslemCekmece({ cekler, hedef, secenekler, kapat, oldu }: {
  cekler: Cek[]; hedef: CekDurum; secenekler: CekSecenekler | null; kapat: () => void; oldu: (mesaj: string, uyarilar: string[]) => void;
}) {
  const { can } = useAuth();
  const ilk = cekler[0];
  const toplu = cekler.length > 1;
  const para = ilk.para;
  const tekPara = cekler.every((c) => c.para === para);
  /** Seçilenler arasında resmî çek var mı: varsa yalnız resmî hesaplar sunulur. */
  const resmiVar = cekler.some((c) => c.resmi);
  const nakitYazar = NAKITLI.includes(hedef);
  const nakitIptal = cekler.some((c) => NAKITLI.includes(c.durum));
  const hesapIster = hedef === 'BANKADA' || hedef === 'TAHSIL' || hedef === 'ODENDI' || hedef === 'TEMINATTA';
  const geriAlma = hedef === 'BANKADA' && cekler.every((c) => c.durum === 'TAHSIL');
  const varsayilanHesap = !toplu ? (hedef === 'ODENDI' ? ilk.kendiHesapId : hedef === 'TAHSIL' || geriAlma ? ilk.hesapId : null) : null;
  const [hesapId, setHesapId] = useState(String(varsayilanHesap ?? ''));
  const [ciro, setCiro] = useState('');
  const [tarih, setTarih] = useState(bugunIso());
  const [not, setNot] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const anahtar = useRef(yeniAnahtar());
  const toplam = cekler.reduce((n, c) => n + c.tutarKurus, 0);
  const hesaplar = (secenekler?.hesaplar ?? []).filter((h) => h.para === para && (h.resmi || !resmiVar));
  const baslik = islemAdi(ilk.durum, hedef, ilk.yon);
  const kasaYok = (nakitYazar || nakitIptal) && !can('cash.write');
  // Toplu tahsilde her çekin kendi bankası varsa hesap seçmek zorunlu değildir.
  const hesapZorunlu = hedef === 'BANKADA' ? !geriAlma : hedef === 'TAHSIL' ? cekler.some((c) => !c.hesapId) : hedef === 'ODENDI' ? cekler.some((c) => !c.kendiHesapId) : false;

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    if (hesapZorunlu && !hesapId) { setHata('Hesabı seçin.'); return; }
    if (hedef === 'CIRO' && !ciro) { setHata('Çeki hangi tedarikçiye ciro ettiğinizi seçin.'); return; }
    setHata(null); setBekliyor(true);
    try {
      const govde = { durum: hedef, hesapId: hesapIster && hesapId ? Number(hesapId) : null, ciroSupplierId: hedef === 'CIRO' ? Number(ciro) : null, tarih, not: not.trim() || null, islemAnahtari: anahtar.current };
      const r = toplu
        ? await api<{ degisen: number; uyarilar: string[] }>('/api/cek/toplu', { method: 'POST', body: JSON.stringify({ ...govde, idler: cekler.map((c) => c.id) }) })
        : await api<{ uyarilar: string[] }>(`/api/cek/${ilk.id}/durum`, { method: 'POST', body: JSON.stringify(govde) });
      const ne = toplu ? `${cekler.length} çek / senet` : `${TUR_AD[ilk.tur]} ${ilk.no}`;
      oldu(`${ne}: ${DURUM_KISA[hedef]}.${nakitYazar ? ` Kasa defterine ${hedef === 'TAHSIL' ? 'giriş' : 'çıkış'} yazıldı.` : nakitIptal ? ' Kasa hareketi iptal edildi.' : ''}`, r.uyarilar ?? []);
      kapat();
    } catch (err) { setHata(err instanceof Error ? err.message : 'İşlem yapılamadı'); }
    finally { setBekliyor(false); }
  }

  return (
    <Cekmece ust={toplu ? `Toplu işlem · ${cekler.length} çek / senet` : `${TUR_AD[ilk.tur]} ${ilk.no}`} baslik={baslik} kapat={kapat} mesgul={bekliyor}>
      <form className="ccd-stack" onSubmit={gonder}>
        <div className="ccd-kasa-icform">
          {toplu
            ? <><b>{cekler.length} çek / senet</b><div className="ccd-meta">{cekler.slice(0, 6).map((c) => c.no).join(', ')}{cekler.length > 6 ? ` ve ${cekler.length - 6} tane daha` : ''}</div></>
            : <><b>{ilk.cariAd ?? ilk.kesideci ?? '—'}</b><div className="ccd-meta">{[ilk.bankaAdi, `vade ${gunYaz(ilk.vadeTarihi)}`, DURUM_KISA[ilk.durum]].filter(Boolean).join(' · ')}</div></>}
          <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{tekPara ? paraYaz(toplam, para) : 'Birden çok para birimi'}</div>
        </div>
        {!tekPara && hesapIster && <Uyari m="Seçilen çekler farklı para birimlerinde. Hesap seçilen işlemleri para birimi başına ayrı yapın." />}
        {kasaYok && <Uyari m="Bu işlem kasa defterine hareket yazar ya da hareketi iptal eder; kasaya yazma yetkiniz yok." />}
        {nakitYazar && <NotSatiri>{hedef === 'TAHSIL'
          ? <>Seçtiğiniz hesaba <b>giriş hareketi</b> yazılır (her çek için ayrı). Nakit girişi yalnız bu adımda doğar. <Ipucu id="cek-nakit-etkisi" /></>
          : <>Seçtiğiniz hesaptan <b>çıkış hareketi</b> yazılır. Nakit çıkışı yalnız bu adımda doğar. <Ipucu id="cek-nakit-etkisi" /></>}</NotSatiri>}
        {hedef === 'CIRO' && <NotSatiri><b>Ciro tahsil değildir.</b> Çek tedarikçiye verilir, borcunuzu kapatır; kasa defterine <b>hareket yazılmaz</b>. <Ipucu id="cek-ciro" /></NotSatiri>}
        {hedef === 'BANKADA' && !geriAlma && <NotSatiri>Çek bankaya tahsile verilir; <b>para henüz gelmedi</b>, kasa hareketi yazılmaz. Banka tahsil edince "Tahsil edildi" deyin.</NotSatiri>}
        {nakitIptal && !nakitYazar && <Uyari m={<>Bu, bir <b>geri almadır</b>: çekin kasa defterindeki hareketi aynı anda iptal edilir, hesap bakiyesi eski hâline döner.</>} />}
        <Hata m={hata} />
        {hesapIster && !geriAlma && (
          <Alan etiket={hedef === 'ODENDI' ? 'Hangi hesabınızdan çıktı' : hedef === 'TAHSIL' ? 'Hangi hesaba girdi' : hedef === 'BANKADA' ? 'Hangi banka hesabına verildi' : 'Hangi bankada (isteğe bağlı)'}
                htmlFor="cek-islem-hesap" zorunlu={hesapZorunlu}
                not={!hesapZorunlu && (hedef === 'TAHSIL' || hedef === 'ODENDI') ? 'Boş bırakırsanız çekin kayıtlı hesabı kullanılır.' : resmiVar && secenekler?.hesaplar.some((h) => !h.resmi) ? 'Resmî çek yalnız resmî hesaba işlenir.' : undefined}>
            <AraSec id="cek-islem-hesap" yukleniyor={!secenekler} deger={hesapId} degis={setHesapId} bosEtiket={hesapZorunlu ? undefined : '— kayıtlı hesap —'} yerTutucu="Hesap ara…" secenekler={hesaplar.map(hesapSec)} />
            {secenekler && hesaplar.length === 0 && <div className="ccd-meta">Bu para biriminde uygun hesap yok. <Link to="/kasa?sekme=tanimlar">Kasa ve Banka › Tanımlar</Link>'dan hesap tanımlayın.</div>}
          </Alan>
        )}
        {hedef === 'CIRO' && (
          <Alan etiket="Kime ciro edildi (tedarikçi)" htmlFor="cek-islem-ciro" zorunlu>
            <AraSec id="cek-islem-ciro" yukleniyor={!secenekler} deger={ciro} degis={setCiro} yerTutucu="Tedarikçi ara…" secenekler={secenekler?.tedarikciler ?? []} />
          </Alan>
        )}
        <Alan etiket="İşlem tarihi" htmlFor="cek-islem-tarih" not={nakitYazar ? 'Kasa hareketi bu tarihle yazılır; bugünden sonra olamaz.' : undefined}>
          <input id="cek-islem-tarih" className="ccd-input" type="date" max={bugunIso()} value={tarih} onChange={(e) => setTarih(e.target.value)} />
        </Alan>
        <Alan etiket="Not" htmlFor="cek-islem-not"><input id="cek-islem-not" className="ccd-input" maxLength={300} value={not} onChange={(e) => setNot(e.target.value)} placeholder="İsteğe bağlı" /></Alan>
        <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor || kasaYok || (!tekPara && hesapIster)}>{bekliyor ? 'İşleniyor…' : baslik}</button>
      </form>
    </Cekmece>
  );
}

/* ─── Ayrıntı: bilgiler, geçerli işlemler, hareket geçmişi ───────────────── */
export function AyrintiCekmece({ cekId, surum, yazar, kapat, islemAc, duzenle, iptalEdildi }: {
  cekId: number; surum: number; yazar: boolean; kapat: () => void; islemAc: (c: Cek, hedef: CekDurum) => void; duzenle: (c: Cek) => void; iptalEdildi: (mesaj: string) => void;
}) {
  const [veri, setVeri] = useState<{ cek: Cek; hareketler: CekHareketi[] } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [resim, setResim] = useState<string | null>(null);
  const [iptalAcik, setIptalAcik] = useState(false);
  const [neden, setNeden] = useState('');
  const [bekliyor, setBekliyor] = useState(false);

  useEffect(() => {
    api<{ cek: Cek; hareketler: CekHareketi[] }>(`/api/cek/${cekId}`).then((r) => { setVeri(r); setHata(null); }).catch((e) => setHata((e as Error).message));
  }, [cekId, surum]);
  const gorsel = veri?.cek.gorsel ?? null;
  useEffect(() => {
    let url: string | null = null; let iptal = false;
    setResim(null);
    if (gorsel) void gorselAl(gorsel).then((u) => { if (iptal) { if (u) URL.revokeObjectURL(u); } else { url = u; setResim(u); } });
    return () => { iptal = true; if (url) URL.revokeObjectURL(url); };
  }, [gorsel]);

  async function iptalEt(e: React.FormEvent) {
    e.preventDefault();
    if (neden.trim().length < 3) { setHata('İptal nedenini yazın (en az 3 harf).'); return; }
    setBekliyor(true); setHata(null);
    try {
      await api(`/api/cek/${cekId}/iptal`, { method: 'POST', body: JSON.stringify({ neden: neden.trim() }) });
      iptalEdildi(`${veri ? `${TUR_AD[veri.cek.tur]} ${veri.cek.no}` : 'Kayıt'} iptal edildi.`); kapat();
    } catch (err) { setHata(err instanceof Error ? err.message : 'İptal edilemedi'); }
    finally { setBekliyor(false); }
  }

  const c = veri?.cek;
  return (
    <Cekmece ust="Çek / Senet" baslik={c ? `${TUR_AD[c.tur]} ${c.no}` : 'Ayrıntı'} kapat={kapat} mesgul={bekliyor} genis>
      <Hata m={hata} />
      {!c ? (!hata && <div className="ccd-meta">Yükleniyor…</div>) : (
        <>
          <div className="ccd-kasa-icform ccd-cek-ozet">
            <div>
              <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <span className={`ccd-badge ${c.iptal ? '' : DURUM_ROZET[c.durum]}`} data-fihrist="cek-durumlari">{c.iptal ? 'İptal edildi' : c.durumAd}</span>
                <span className="ccd-badge">{c.yon === 'ALINAN' ? 'Alınan' : 'Verilen'}</span>
                {!c.resmi && <span className="ccd-badge ccd-badge-warning">gayriresmî</span>}
              </div>
              <div style={{ fontSize: 24, fontWeight: 600, marginTop: 6 }}>{paraYaz(c.tutarKurus, c.para)}</div>
              <div className="ccd-meta">Vade {gunYaz(c.vadeTarihi)}{c.bekliyor && <> · <b className={c.kalanGun < 0 ? 'ccd-kasa-cikis' : undefined}>{kalanYaz(c.kalanGun)}</b></>}</div>
            </div>
            <dl className="ccd-cek-bilgi">
              <dt>{c.yon === 'ALINAN' ? 'Kimden' : 'Kime'}</dt><dd>{c.customerId ? <Link to={`/crm/${c.customerId}`}>{c.cariAd}</Link> : (c.cariAd ?? '—')}</dd>
              {c.kesideci && <><dt>{c.yon === 'ALINAN' ? 'Keşideci' : 'Kime (ad)'}</dt><dd>{c.kesideci}</dd></>}
              {(c.bankaAdi || c.sube) && <><dt>Banka</dt><dd>{[c.bankaAdi, c.sube, c.hesapNo].filter(Boolean).join(' · ')}</dd></>}
              {c.duzenlemeTarihi && <><dt>Düzenleme</dt><dd>{gunYaz(c.duzenlemeTarihi)}</dd></>}
              {c.hesapAd && <><dt>{c.durum === 'TAHSIL' || c.durum === 'ODENDI' ? 'Kasa hesabı' : 'Bulunduğu hesap'}</dt><dd>{c.hesapAd}</dd></>}
              {c.kendiHesapAd && <><dt>Kesildiği hesap</dt><dd>{c.kendiHesapAd}</dd></>}
              {c.ciroAd && <><dt>Ciro edilen</dt><dd>{c.ciroAd}</dd></>}
              {c.orderId && <><dt>Sipariş</dt><dd><Link to={`/siparis/${c.orderId}`}>{c.orderNo}</Link></dd></>}
              {c.not && <><dt>Not</dt><dd>{c.not}</dd></>}
              {c.iptal && <><dt>İptal nedeni</dt><dd>{c.iptalNeden}</dd></>}
            </dl>
          </div>

          {yazar && !c.iptal && (
            <div className="ccd-stack-sm">
              <div className="ccd-fieldset-title">Yapılabilecek işlemler <Ipucu id="cek-durumlari" /></div>
              {c.gecisler.length === 0 ? <div className="ccd-meta">Bu durumdan yapılabilecek işlem yok.</div> : (
                <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {c.gecisler.map((g) => (
                    <button key={g} type="button" className={`ccd-btn ccd-btn-sm${g === 'TAHSIL' || g === 'ODENDI' ? ' ccd-btn-primary' : ''}`} onClick={() => islemAc(c, g)}>{islemAdi(c.durum, g, c.yon)}</button>
                  ))}
                </div>
              )}
              <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => duzenle(c)}>Bilgileri düzenle</button>
                {!NAKITLI.includes(c.durum) && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setIptalAcik((a) => !a)}>Kaydı iptal et</button>}
              </div>
              {iptalAcik && (
                <form className="ccd-kasa-icform ccd-stack-sm" onSubmit={iptalEt}>
                  <div className="ccd-meta">Yanlış girilmiş kayıt içindir. Kayıt silinmez; listelerden ve toplamlardan çıkar, "Geçmiş" sekmesinde iptal edilmiş olarak durur.</div>
                  <input className="ccd-input" maxLength={300} autoFocus value={neden} onChange={(e) => setNeden(e.target.value)} placeholder="İptal nedeni (zorunlu)" aria-label="İptal nedeni" />
                  <button className="ccd-btn ccd-btn-danger ccd-btn-sm" disabled={bekliyor}>{bekliyor ? 'İptal ediliyor…' : 'İptal et'}</button>
                </form>
              )}
            </div>
          )}

          {resim && <a href={resim} target="_blank" rel="noreferrer" className="ccd-cek-gorsel"><img src={resim} alt={`${TUR_AD[c.tur]} ${c.no} fotoğrafı`} /></a>}

          <div className="ccd-stack-sm">
            <div className="ccd-fieldset-title">Hareket geçmişi</div>
            <div className="ccd-table-wrap ccd-kasa-tablo ccd-kasa-tablo-ic">
              <table className="ccd-table">
                <thead><tr><th className="ccd-th">Tarih</th><th className="ccd-th">Durum</th><th className="ccd-th">Hesap / cari</th><th className="ccd-th">Kasa</th><th className="ccd-th">Kim</th></tr></thead>
                <tbody>
                  {veri.hareketler.map((h) => (
                    <tr key={h.id} className="ccd-tr">
                      <td className="ccd-td ccd-td-nowrap">{gunYaz(h.tarih)}</td>
                      <td className="ccd-td">{h.oncekiAd ? <><span className="ccd-muted">{DURUM_KISA[h.oncekiDurum!]} →</span> <b>{DURUM_KISA[h.yeniDurum]}</b></> : <b>{h.not ?? 'Kayıt açıldı'}</b>}{h.oncekiAd && h.not && <div className="ccd-meta">{h.not}</div>}</td>
                      <td className="ccd-td">{h.tedarikciAd ?? h.hesapAd ?? '—'}</td>
                      <td className="ccd-td">{h.nakit === 'YAZILDI' ? <span className="ccd-badge ccd-badge-success">hareket yazıldı</span> : h.nakit === 'IPTAL' ? <span className="ccd-badge ccd-badge-warning">hareket iptal</span> : <span className="ccd-muted">—</span>}</td>
                      <td className="ccd-td">{h.kim ?? 'sistem'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </Cekmece>
  );
}

/* ─── Vade uyarısı günleri ───────────────────────────────────────────────── */
export function UyariAyarCekmece({ gunler, kapat, kaydedildi }: { gunler: number[]; kapat: () => void; kaydedildi: (mesaj: string) => void }) {
  const [metin, setMetin] = useState(gunler.join(', '));
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    const liste = metin.split(/[,;\s]+/).filter(Boolean).map(Number);
    if (liste.length === 0 || liste.some((n) => !Number.isInteger(n) || n < 1 || n > 90)) { setHata('Günleri virgülle ayırarak yazın (1 ile 90 arası tam sayı). Örn. 7, 1'); return; }
    setBekliyor(true); setHata(null);
    try {
      const r = await api<{ uyariGunleri: number[] }>('/api/cek/ayar', { method: 'PUT', body: JSON.stringify({ uyariGunleri: liste }) });
      kaydedildi(`Vade uyarısı: vadeye ${r.uyariGunleri.join(' ve ')} gün kala, vade günü ve vade geçtikten sonra haftada bir.`); kapat();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
    finally { setBekliyor(false); }
  }
  async function simdi() {
    setBekliyor(true); setHata(null);
    try { const r = await api<{ uretilen: number }>('/api/cek/vade-uyari', { method: 'POST' }); setBilgi(r.uretilen > 0 ? `${r.uretilen} uyarı üretildi.` : 'Yeni uyarı yok: vadesi yaklaşan her çek için bugünkü uyarı zaten üretilmiş.'); }
    catch (err) { setHata(err instanceof Error ? err.message : 'Denetlenemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <Cekmece ust="Çek / Senet" baslik="Vade uyarısı" kapat={kapat} mesgul={bekliyor}>
      <form className="ccd-stack" onSubmit={kaydet}>
        <div className="ccd-meta">Portföydeki, bankadaki ve teminattaki alınan çekler ile ödenmemiş verilen çekler için panel uyarı üretir (Telegram bağlıysa oraya da düşer). Bir çek için günde en çok bir uyarı çıkar.</div>
        <Hata m={hata} /><Bilgi m={bilgi} />
        <Alan etiket="Vadeye kaç gün kala uyarsın" htmlFor="cek-uyari-gun" not="Virgülle ayırın. Vade günü ve vade geçtikten sonra haftada bir ayrıca uyarır.">
          <input id="cek-uyari-gun" className="ccd-input" inputMode="numeric" value={metin} onChange={(e) => setMetin(e.target.value)} placeholder="7, 1" />
        </Alan>
        <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>Kaydet</button>
        <button type="button" className="ccd-btn ccd-btn-sm" onClick={simdi} disabled={bekliyor}>Şimdi denetle</button>
      </form>
    </Cekmece>
  );
}
